<template>
    <div class="list-container">
        <v-row>
            <v-col cols="12" class="list-title">
                {{ activeStudents }} {{ $t('admin.active_students') }}
            </v-col>
            <v-col cols="7">
                <kimsa-text-field
                    @change="search = $event"
                    :value="search"
                    :placeholder="$t('admin.search_coach')"
                    color="accent" dense outlined
                    prepend-inner-icon="$search"
                />
            </v-col>
            <v-col cols="5">
                <v-btn class="b-primary" depressed @click="showInviteDialog" :disabled="!currentCoach">
                    {{ $t('admin.add_student') }}
                </v-btn>
            </v-col>
        </v-row>
        <loading-spinner v-if="loading"/>
        <v-row v-else>
            <v-col cols="12" md="4" class="p-relative" style="min-height: 150px">
                <div class="title-section">
                    {{ $tc('coach.coach',2) }}
                </div>
                <div v-if="coachesFiltered.length === 0" class="select-coach">
                    {{ $t('admin.no_coaches') }}
                </div>
                <company-coach-card
                    v-for="coach in coachesFiltered"
                    :key="coach.id"
                    :coach="coach"
                    @click="onClickCoach(coach)"
                    :is-selected="isCoachSelected(coach)"
                    class="mb-2"
                />
            </v-col>
            <v-col cols="12" md="8" class="students-container p-relative" style="min-height: 150px">
                <div v-if="currentCoach">
                    <div class="title-section">
                        {{ $tc('student.student',2) }}
                    </div>
                    <div class="mb-3">
                        <kimsa-text-field
                            @change="searchStudent = $event"
                            :value="searchStudent"
                            :placeholder="$t('admin.search_student')"
                            color="accent" dense outlined
                            prepend-inner-icon="$search"
                        />
                    </div>
                    <loading-spinner v-if="loadingStudents"/>
                    <v-row v-else dense>
                        <v-col cols="12" md="6" lg="4" v-for="(student,i) in studentsFiltered" :key="i">
                            <coach-student-menu-card
                                :student="student"
                                @click:profile="toStudentProfile(student)"
                                @click:change="showChangeCoach(student)"
                                @click:delete="showDeleteSubscription(student)"
                            />
                        </v-col>
                        <v-col v-if="students.length === 0" class="select-coach">
                            {{ $t('common.no_students') }}
                        </v-col>
                    </v-row>
                </div>
                <div v-else class="select-coach">
                    {{ $t('admin.select_coach') }}
                </div>
            </v-col>
        </v-row>

        <!-- DELETE STUDENT FOR TRAINING -->
        <kimsa-confirm
            ref="deleteSubscriptionDialog"
            :title="$t('coach.delete_student_subscription_title')"
            :content="$t('coach.delete_student_subscription_body')"
            :loading="deletingSubscription"
            :persistent="false"
            @confirm="confirmSubscriptionDelete"
            :close-on-confirm="false"
            @cancel="currentStudent = null"
            :alert-mode="true"
        />

        <!-- CHANGE STUDENT COACH -->
        <student-change-coach-dialog
            ref="studentChangeCoachDialog"
            :current-coach="currentCoach"
            :current-student="currentStudent"
            :coaches="coaches"
            @changed="loadStudents"
        />

        <!-- INVITE STUDENT -->
        <invite-student-dialog :coach="currentCoach" ref="invite"/>
    </div>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import {db} from "@/utils/Firebase";
import CompanyCoachCard from "@/components/company/CompanyCoachCard";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import CoachStudentMenuCard from "@/components/company/CoachStudentMenuCard";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import StudentChangeCoachDialog from "@/components/company/StudentChangeCoachDialog";
import InviteStudentDialog from "@/components/coach/InviteStudentDialog";

export default {
    name: "ListStudents",
    components: {
        InviteStudentDialog,
        StudentChangeCoachDialog,
        KimsaConfirm, CoachStudentMenuCard, LoadingSpinner, CompanyCoachCard, KimsaTextField},
    data() {
        return {
            loading: false,
            search: '',
            coaches: [],
            currentCoach: null,
            loadingStudents: false,
            students: [],
            deletingSubscription: false,
            currentStudent: null,
            searchStudent: '',
        }
    },
    computed: {
        activeStudents(vm = this) {
            if(vm.coaches.length === 0) return 0
            let students = 0
            vm.coaches.forEach(coach => {
                students += coach.activeStudents || 0
            })
            return students
        },
        coachesFiltered(vm = this) {
            if(!vm.search) return vm.coaches
            return vm.coaches.filter(coach => coach?.name?.toLowerCase().includes(vm.search))
        },
        studentsFiltered(vm = this) {
            if(!vm.searchStudent) return vm.students
            return vm.students.filter(student => student?.name?.toLowerCase().includes(vm.searchStudent))
        },
    },
    async mounted() {
        const vm = this
        await vm.loadCoaches()
    },
    methods: {
        async loadCoaches() {
            const vm = this
            vm.loading = true

            let coachesRef = await db.collection('users')
                .where('coachSubscription.companyId', '==', vm.currentUser.uid).get()
            // console.log('coachesRef', coachesRef)

            vm.coaches = coachesRef.docs.map(coach => ({
                id: coach.id,
                ...coach.data()
            }))

            if(vm.$route.query?.coach) vm.currentCoach = vm.coaches.find(coach => coach.id === vm.$route.query.coach)

            vm.loading = false
        },
        async loadStudents() {
            const vm = this
            vm.loadingStudents = true
            vm.students = []
            let studentsRef = await db.collection('users').where('studentSubscription.coachId', '==', vm.currentCoach.id).get()

            vm.students = studentsRef.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }))

            // console.log('vm.students', vm.students)

            vm.loadingStudents = false
        },
        onClickCoach(coach) {
            const vm = this
            vm.currentCoach = coach
        },
        isCoachSelected(coach) {
            const vm = this
            return coach?.id === vm.currentCoach?.id
        },
        toStudentProfile(student) {
            const vm = this
            vm.$router.push({name: 'company.students.profile', params: {id: student.id}})
        },
        showChangeCoach(student) {
            const vm = this
            vm.currentStudent = student
            vm.$refs.studentChangeCoachDialog.show()
        },
        showDeleteSubscription(student) {
            const vm = this
            vm.currentStudent = student
            vm.$refs.deleteSubscriptionDialog.show()
        },
        async confirmSubscriptionDelete() {
            const vm = this
            vm.deletingSubscription = true

            let subscriptionId = vm.currentStudent?.studentSubscription?.id || null
            if(subscriptionId) await db.collection('subscriptions').doc(subscriptionId).delete()

            let payload = {studentSubscription: null}

            if(vm.currentStudent?.group) {
                payload.group = null
                let currentGroup = await db.collection('groups').doc(vm.currentStudent.group).get()
                let students = currentGroup.data().students.filter(st => st !== vm.currentStudent.id)
                await db.collection('groups').doc(vm.student.group).update({students: students})
            }
            if(vm.currentStudent?.team) {
                payload.team = null
                let currentTeamRef = await db.collection('teams').doc(vm.currentStudent.team.id).get()
                let currentTeam = {id: currentTeamRef.id, ...currentTeamRef.data()}
                let currentGroup = currentTeam?.groups?.find(group => vm.currentStudent.team.subgroupId === group.id)
                let students = currentGroup.students.filter(st => st !== vm.studentId)
                let groups = JSON.parse(JSON.stringify(currentTeam?.groups))
                let findGroupIndex = currentTeam?.groups?.findIndex(group => group.id === vm.currentStudent.team.subgroupId || null)
                groups[findGroupIndex].students = students
                await db.collection('teams').doc(vm.currentStudent.team.id).update({groups: groups})
            }

            await db.collection('users').doc(vm.studentId).update(payload)

            vm.deletingSubscription = false
            vm.$refs.deleteSubscriptionDialog.hide()
            vm.toast(vm.$t('coach.student_subscription_deleted'))

            vm.currentStudent = null
            await vm.loadStudents()
        },
        showInviteDialog() {
            const vm = this
            vm.$refs.invite.show()
        },
    },
    watch: {
        currentCoach() {
            this.loadStudents()
        }
    }
}
</script>

<style scoped>

.list-container {
    background: #FFFFFF;
    box-shadow: 0px 4px 8px rgba(217, 217, 217, 0.19);
    border-radius: 4px;
    padding: 24px;
}

.list-title {
    font-weight: 700;
    font-size: 24px;
    line-height: 29px;
    color: #333333;
}

.students-container {
    background: linear-gradient(180deg, #FFFFFF 0%, #F9F9F9 100%);
    border-radius: 14px;
}

.select-coach {
    position: absolute;
    top: 40%;
    left: 50%;
    transform: translate(-50%, -40%);
    text-align: center;
    font-size: 11px;
}

.title-section {
    font-weight: 700;
    margin-bottom: 8px;
    color: #333333;
    font-size: 16px;
    text-align: left;
}

</style>